import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import SellerLayout from "@/components/layouts/SellerLayout";
import CloudinaryUpload from "@/components/CloudinaryUpload";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Plus, Package, Pencil, Trash2, CheckCircle, Clock, AlertCircle, ChevronDown, BarChart3, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const SellerProducts = () => {
  const { user, getAuthHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [priceCompareOpen, setPriceCompareOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    images: "",
    category: "",
    stock: "",
    commission_percent: "",
    // Price comparison fields
    flipkart_price: "",
    flipkart_url: "",
    amazon_price: "",
    amazon_url: "",
    other_platform_name: "",
    other_platform_price: "",
    other_platform_url: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        axios.get(`${API}/seller/products`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categories`),
      ]);
      setProducts(prodRes.data.products || []);
      setCategories(catRes.data.categories || []);
    } catch (error) {
      toast.error("Failed to load products");
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      price: "",
      images: "",
      category: "",
      stock: "",
      commission_percent: "",
      flipkart_price: "",
      flipkart_url: "",
      amazon_price: "",
      amazon_url: "",
      other_platform_name: "",
      other_platform_price: "",
      other_platform_url: "",
    });
    setEditingProduct(null);
    setPriceCompareOpen(false);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      images: product.images.join(", "),
      category: product.category,
      stock: product.stock.toString(),
      commission_percent: product.commission_percent.toString(),
      flipkart_price: product.price_comparison?.flipkart_price?.toString() || "",
      flipkart_url: product.price_comparison?.flipkart_url || "",
      amazon_price: product.price_comparison?.amazon_price?.toString() || "",
      amazon_url: product.price_comparison?.amazon_url || "",
      other_platform_name: product.price_comparison?.other_platform_name || "",
      other_platform_price: product.price_comparison?.other_platform_price?.toString() || "",
      other_platform_url: product.price_comparison?.other_platform_url || "",
    });
    setPriceCompareOpen(!!product.price_comparison);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Build price comparison object if any prices are provided
    const priceComparison = {};
    if (form.flipkart_price) {
      priceComparison.flipkart_price = parseFloat(form.flipkart_price);
      priceComparison.flipkart_url = form.flipkart_url || null;
    }
    if (form.amazon_price) {
      priceComparison.amazon_price = parseFloat(form.amazon_price);
      priceComparison.amazon_url = form.amazon_url || null;
    }
    if (form.other_platform_price && form.other_platform_name) {
      priceComparison.other_platform_name = form.other_platform_name;
      priceComparison.other_platform_price = parseFloat(form.other_platform_price);
      priceComparison.other_platform_url = form.other_platform_url || null;
    }

    const payload = {
      name: form.name,
      description: form.description,
      price: parseFloat(form.price),
      images: form.images.split(",").map((s) => s.trim()),
      category: form.category,
      stock: parseInt(form.stock),
      commission_percent: parseFloat(form.commission_percent),
      price_comparison: Object.keys(priceComparison).length > 0 ? priceComparison : null,
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/seller/products/${editingProduct.id}`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success("Product updated! Pending re-approval.");
      } else {
        await axios.post(`${API}/seller/products`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success("Product submitted for approval!");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
    setSubmitting(false);
  };

  const handleDelete = async (productId) => {
    if (!confirm("Are you sure you want to delete this product?")) return;

    try {
      await axios.delete(`${API}/seller/products/${productId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const isCheapest = (product) => {
    if (!product.price_comparison) return false;
    const prices = [product.price];
    if (product.price_comparison.flipkart_price) prices.push(product.price_comparison.flipkart_price);
    if (product.price_comparison.amazon_price) prices.push(product.price_comparison.amazon_price);
    if (product.price_comparison.other_platform_price) prices.push(product.price_comparison.other_platform_price);
    return product.price === Math.min(...prices);
  };

  if (!user?.is_approved) {
    return (
      <SellerLayout title="Products">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Account Not Approved Yet
            </h3>
            <p className="text-gray-500">
              Your seller account is pending approval. You'll be able to add products once approved.
            </p>
          </CardContent>
        </Card>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout title="Products">
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-500">{products.length} products</p>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button
              className="bg-secondary-500 !text-primary-900 hover:bg-secondary-400"
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProduct ? "Edit Product" : "Add New Product"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Product Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Enter product name"
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Enter product description"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Your Price (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="0"
                    required
                    data-testid="product-price-input"
                  />
                </div>
                <div>
                  <Label>Stock</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.stock}
                    onChange={(e) => setForm({ ...form, stock: e.target.value })}
                    placeholder="0"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Product Images (upload one or more)</Label>
                <CloudinaryUpload
                  value={form.images}
                  onChange={(url) => {
                    // Append to comma list (or replace if empty)
                    const list = form.images
                      ? form.images.split(",").map((s) => s.trim()).filter(Boolean)
                      : [];
                    if (url) list.push(url);
                    setForm({ ...form, images: list.join(", ") });
                  }}
                  folder="seller_products/"
                  resourceType="image"
                  testId="seller-product-image-upload"
                  allowUrlPaste={false}
                />
                <Input
                  value={form.images}
                  onChange={(e) => setForm({ ...form, images: e.target.value })}
                  placeholder="Or paste URL(s), comma separated"
                  className="mt-2 text-xs"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Tip: upload multiple images one-by-one — they will be appended automatically.
                </p>
              </div>
              <div>
                <Label>Commission % (you offer to platform)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={form.commission_percent}
                  onChange={(e) => setForm({ ...form, commission_percent: e.target.value })}
                  placeholder="10"
                  required
                />
              </div>

              {/* Price Comparison Section */}
              <Collapsible open={priceCompareOpen} onOpenChange={setPriceCompareOpen}>
                <CollapsibleTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Price Comparison (Optional)
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${priceCompareOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4 space-y-4 p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-500">
                    Add competitor prices to show customers your price advantage
                  </p>
                  
                  {/* Flipkart */}
                  <div className="p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 bg-[#2874F0] rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">F</span>
                      </div>
                      <span className="font-medium">Flipkart</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Price (₹)</Label>
                        <Input
                          type="number"
                          value={form.flipkart_price}
                          onChange={(e) => setForm({ ...form, flipkart_price: e.target.value })}
                          placeholder="Flipkart price"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Product URL</Label>
                        <Input
                          value={form.flipkart_url}
                          onChange={(e) => setForm({ ...form, flipkart_url: e.target.value })}
                          placeholder="https://flipkart.com/..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Amazon */}
                  <div className="p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 bg-[#FF9900] rounded flex items-center justify-center">
                        <span className="text-black text-xs font-bold">A</span>
                      </div>
                      <span className="font-medium">Amazon</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Price (₹)</Label>
                        <Input
                          type="number"
                          value={form.amazon_price}
                          onChange={(e) => setForm({ ...form, amazon_price: e.target.value })}
                          placeholder="Amazon price"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Product URL</Label>
                        <Input
                          value={form.amazon_url}
                          onChange={(e) => setForm({ ...form, amazon_url: e.target.value })}
                          placeholder="https://amazon.in/..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Other Platform */}
                  <div className="p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 bg-gray-500 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">+</span>
                      </div>
                      <span className="font-medium">Other Platform</span>
                    </div>
                    <div className="mb-2">
                      <Label className="text-xs">Platform Name</Label>
                      <Input
                        value={form.other_platform_name}
                        onChange={(e) => setForm({ ...form, other_platform_name: e.target.value })}
                        placeholder="e.g., Myntra, Meesho"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Price (₹)</Label>
                        <Input
                          type="number"
                          value={form.other_platform_price}
                          onChange={(e) => setForm({ ...form, other_platform_price: e.target.value })}
                          placeholder="Price"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Product URL</Label>
                        <Input
                          value={form.other_platform_url}
                          onChange={(e) => setForm({ ...form, other_platform_url: e.target.value })}
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <Button
                type="submit"
                className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400"
                disabled={submitting}
                data-testid="submit-product-btn"
              >
                {submitting
                  ? "Saving..."
                  : editingProduct
                  ? "Update Product"
                  : "Submit for Approval"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="aspect-square bg-gray-200 rounded-xl mb-4" />
                <div className="h-4 bg-gray-200 rounded mb-2" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : products.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => (
            <Card key={product.id} className="border-0 shadow-sm overflow-hidden">
              <div className="aspect-video bg-gray-100 relative">
                {product.images?.[0] && (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                )}
                <Badge
                  className={`absolute top-2 right-2 ${
                    product.is_approved
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {product.is_approved ? (
                    <>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Approved
                    </>
                  ) : (
                    <>
                      <Clock className="w-3 h-3 mr-1" />
                      Pending
                    </>
                  )}
                </Badge>
                {isCheapest(product) && (
                  <Badge className="absolute top-2 left-2 bg-green-500 text-white">
                    <TrendingDown className="w-3 h-3 mr-1" />
                    Lowest
                  </Badge>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">
                  {product.name}
                </h3>
                <p className="text-sm text-gray-500 mb-2 line-clamp-2">
                  {product.description}
                </p>
                
                {/* Price Display */}
                <div className="mb-3">
                  <p className="text-lg font-bold text-primary-600">₹{product.price}</p>
                  
                  {/* Price Comparison Display */}
                  {product.price_comparison && (
                    <div className="mt-2 space-y-1 text-xs">
                      {product.price_comparison.flipkart_price && (
                        <div className="flex items-center justify-between text-gray-500">
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 bg-[#2874F0] rounded text-white text-[6px] flex items-center justify-center">F</span>
                            Flipkart
                          </span>
                          <span className={product.price < product.price_comparison.flipkart_price ? 'line-through' : 'text-red-600 font-medium'}>
                            ₹{product.price_comparison.flipkart_price}
                          </span>
                        </div>
                      )}
                      {product.price_comparison.amazon_price && (
                        <div className="flex items-center justify-between text-gray-500">
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 bg-[#FF9900] rounded text-black text-[6px] flex items-center justify-center">A</span>
                            Amazon
                          </span>
                          <span className={product.price < product.price_comparison.amazon_price ? 'line-through' : 'text-red-600 font-medium'}>
                            ₹{product.price_comparison.amazon_price}
                          </span>
                        </div>
                      )}
                      {product.price_comparison.other_platform_name && (
                        <div className="flex items-center justify-between text-gray-500">
                          <span>{product.price_comparison.other_platform_name}</span>
                          <span className={product.price < product.price_comparison.other_platform_price ? 'line-through' : 'text-red-600 font-medium'}>
                            ₹{product.price_comparison.other_platform_price}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-400">Stock: {product.stock}</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(product)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(product.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products Yet</h3>
            <p className="text-gray-500 mb-4">Start adding products to your store</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-secondary-500 !text-primary-900 hover:bg-secondary-400"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Product
            </Button>
          </CardContent>
        </Card>
      )}
    </SellerLayout>
  );
};

export default SellerProducts;
